import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../../../../hooks";
import { ActiveSessionApi } from "../../../../../api/revi/activeSessionApi";
import { ReviTherapyApi } from "../../../../../api/revi/reviTherapyApi";
import { getTherapistIdFromUser } from "../../Page_Therapist_Evaluaciones/useEvaluacionesController/utils";

const api = new ActiveSessionApi();
const therapyApi = new ReviTherapyApi();

function isTherapyIncomplete(therapy) {
  if (!therapy) return true;

  const hasScript = typeof therapy.script === "string" && therapy.script.trim();
  const hasObjectives =
    (typeof therapy.objectives === "string" && therapy.objectives.trim()) ||
    (Array.isArray(therapy.objectives) && therapy.objectives.length > 0);

  // (Opcional) descripción, por si existe en algún modelo
  const hasDescription =
    (typeof therapy.description === "string" && therapy.description.trim()) ||
    (typeof therapy.generalDescription === "string" &&
      therapy.generalDescription.trim());

  return !hasScript && !hasObjectives && !hasDescription;
}

export function useTherapistSesionController({ messageApi } = {}) {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { accessToken, user } = useAuth();

  const therapistId = useMemo(() => getTherapistIdFromUser(user), [user]);

  const notify = useMemo(() => {
    return (
      messageApi || {
        success: () => {},
        error: () => {},
        warning: () => {},
        info: () => {},
      }
    );
  }, [messageApi]);

  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);

      const res = await api.getDetail(accessToken, sessionId);
      let sess = res?.session || null;

      // ✅ Hidratamos la terapia si viene “light” desde activeSession
      // (en tu ejemplo viene solo _id, title y scenes)
      if (sess?.therapy?._id && isTherapyIncomplete(sess.therapy)) {
        try {
          const therapyId = sess.therapy._id;

          // Guardamos las escenas de sesión para NO perder evaluaciones completas
          const sessionScenes = Array.isArray(sess?.therapy?.scenes)
            ? sess.therapy.scenes
            : [];

          const fullResponse = await therapyApi.getById(accessToken, therapyId);
          const fullTherapy = fullResponse?.therapy || null;

          if (fullTherapy && fullTherapy._id) {
            sess = {
              ...sess,
              therapy: {
                ...fullTherapy, // ✅ aporta script/objectives/image/categories...
                ...sess.therapy, // ✅ preserva lo que venga “especial” de sesión
                scenes: sessionScenes, // ✅ prioridad a scenes de sesión
              },
            };
          }
        } catch (e) {
          // No rompemos la sesión si falla el refetch
          // eslint-disable-next-line no-console
          console.warn("No se pudo hidratar la terapia completa:", e);
        }
      }

      setSession(sess);
    } catch (e) {
      notify.error(e?.message || "No se pudo cargar la sesión");
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, [accessToken, sessionId, notify]);

  useEffect(() => {
    if (!accessToken || !sessionId) return;
    load();
  }, [accessToken, sessionId, load]);

  const currentStep = useMemo(() => {
    const idx = Number(session?.currentSceneIndex || 0);
    return session?.therapy?.scenes?.[idx] || null;
  }, [session]);

  const launchVR = useCallback(
    async (options) => {
      try {
        await api.launchScene(accessToken, sessionId, options);
        notify.success("Sala VR lista");
        await load();
      } catch (e) {
        notify.error(e?.message || "No se pudo lanzar la sala VR");
      }
    },
    [accessToken, sessionId, load, notify],
  );

  const nextScene = useCallback(async () => {
    try {
      await api.nextScene(accessToken, sessionId);
      notify.success("Avanzado a la siguiente sala");
      await load();
    } catch (e) {
      notify.error(e?.message || "No se pudo avanzar");
    }
  }, [accessToken, sessionId, load, notify]);

  const closeSession = useCallback(async () => {
    try {
      await api.close(accessToken, sessionId);
      notify.success("Sesión finalizada");
      navigate("/portal/therapist/terapia");
    } catch (e) {
      notify.error(e?.message || "No se pudo finalizar");
    }
  }, [accessToken, sessionId, navigate, notify]);

  const submitEvaluationForPatients = useCallback(
    async ({ evaluationId, patientIds, answers }) => {
      try {
        await Promise.all(
          patientIds.map((patientId) =>
            api.submitEvaluation(accessToken, {
              sessionId,
              patientId,
              evaluationId,
              answers,
              comments: "",
            }),
          ),
        );
        notify.success("Evaluación guardada");
      } catch (e) {
        notify.error(e?.message || "No se pudo guardar la evaluación");
      }
    },
    [accessToken, sessionId, notify],
  );

  const openRoomInNewTab = useCallback(() => {
    const url = session?.hubsRoomUrl;
    if (!url) return notify.warning("No hay URL de sala todavía");
    window.open(url, "_blank", "noopener,noreferrer");
  }, [session?.hubsRoomUrl, notify]);

  return {
    loading,
    session,
    currentStep,
    accessToken,
    therapistId,
    launchVR,
    nextScene,
    closeSession,
    submitEvaluationForPatients,
    openRoomInNewTab,
    render: () => null,
  };
}
